import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { Check } from 'lucide-react';

function getPasswordStrength(pw) {
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) score++;
  if (/\d/.test(pw)) score++;
  if (/[^a-zA-Z0-9]/.test(pw)) score++;
  return score; // 0-5
}

const strengthLabels = ['', 'Weak', 'Fair', 'Good', 'Strong', 'Excellent'];
const strengthColors = ['', '#F43F5E', '#F59E0B', '#F59E0B', '#10B981', '#10B981'];

export default function SignupPage() {
  const { signUp } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const strength = getPasswordStrength(form.password);
  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (form.password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    setLoading(true);
    const { error: err } = await signUp(form.email, form.password, {
      first_name: form.firstName,
      last_name: form.lastName,
    });

    if (err) {
      setError(err.message);
      setLoading(false);
      return;
    }

    setSuccess(true);
    setLoading(false);
  };

  const inputClass = 'w-full px-3.5 py-[11px] bg-tertiary border border-strong rounded-[10px] text-primary-c text-[13px] font-[inherit] transition-all focus:outline-none focus:border-[#0DA7CA] focus:shadow-[0_0_0_3px_rgba(13, 167, 202,0.15)]';

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-primary p-4">
        <div className="w-full max-w-[420px] text-center">
          <div className="w-[48px] h-[48px] bg-success-soft text-success rounded-[14px] flex items-center justify-center mx-auto mb-4">
            <Check size={24} strokeWidth={2.5} />
          </div>
          <h1 className="text-[22px] font-bold text-primary-c mb-2">Check your email</h1>
          <p className="text-[13px] text-muted-c mb-6">
            We sent a confirmation link to <strong className="text-primary-c">{form.email}</strong>.
            Click the link to activate your account.
          </p>
          <Link to="/login" className="text-[13px] font-semibold text-accent-light hover:text-accent transition-colors">
            Back to sign in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-primary p-4">
      <div className="w-full max-w-[420px]">
        <div className="flex flex-col items-center mb-8">
          <img src="/logo.svg" alt="MediLink" className="h-[64px] w-auto mb-4" />
          <h1 className="text-[22px] font-bold text-primary-c">Create an account</h1>
          <p className="text-[13px] text-muted-c mt-1">Get started with MediLink</p>
        </div>

        <div className="bg-elevated border border-theme rounded-xl p-6 md:p-7 shadow-lg">
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="px-4 py-3 rounded-[10px] bg-danger-soft text-danger text-[13px] font-medium">
                {error}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[12px] font-semibold text-secondary-c mb-2">First Name</label>
                <input
                  type="text"
                  className={inputClass}
                  placeholder="John"
                  value={form.firstName}
                  onChange={e => update('firstName', e.target.value)}
                  required
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-[12px] font-semibold text-secondary-c mb-2">Last Name</label>
                <input
                  type="text"
                  className={inputClass}
                  placeholder="Doe"
                  value={form.lastName}
                  onChange={e => update('lastName', e.target.value)}
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-[12px] font-semibold text-secondary-c mb-2">Email</label>
              <input
                type="email"
                className={inputClass}
                placeholder="you@example.com"
                value={form.email}
                onChange={e => update('email', e.target.value)}
                required
              />
            </div>

            <div>
              <label className="block text-[12px] font-semibold text-secondary-c mb-2">Password</label>
              <input
                type="password"
                className={inputClass}
                placeholder="Minimum 8 characters"
                value={form.password}
                onChange={e => update('password', e.target.value)}
                required
                minLength={8}
              />
              {form.password && (
                <div className="mt-2 flex items-center gap-2">
                  <div className="flex-1 h-1 rounded-full bg-tertiary overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${(strength / 5) * 100}%`,
                        background: strengthColors[strength],
                      }}
                    />
                  </div>
                  <span className="text-[11px] font-medium" style={{ color: strengthColors[strength] }}>
                    {strengthLabels[strength]}
                  </span>
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-[11px] rounded-[10px] text-[13px] font-semibold text-white gradient-accent shadow-glow-lg hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </button>
          </form>
        </div>

        <p className="text-center text-[13px] text-muted-c mt-5">
          Already have an account?{' '}
          <Link to="/login" className="font-semibold text-accent-light hover:text-accent transition-colors">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
